// FLOW MATRIX — providers × payers as ONE TABLE whose cells carry the share as ink
// (docs/R5-NETWORK-DESIGN.md §4.3). The record's network is a dense weighted bipartite graph with
// a few hubs and many leaves; drawn as node-link it is a hairball whose edge labels pile up at
// one x. A matrix separates every relation spatially, sorts both axes, and lets the operator
// read a row (one provider's payers) or a column (one payer's providers) the way a table is
// read — "across rows and down columns" (Knaflic, Storytelling with Data, p. 40) — while the
// heatmap's saturation still gives the eye somewhere to land (p. 42). One ink; its saturation is
// the share (never a category hue). A count or an amount prints as a figure with no ink, because
// a count is not a share and must never look like one; a relation with no figure is ▪; a
// proposal nobody has held is ◇, in the attend tone the claim mark already owns.
import { useMemo } from 'react';
import { Att, Lnk, Ref, Sheet, Tbl } from '../PageKit.js';
import { useLocale, useT } from '../../i18n/index.js';
import {
  linkLabel, quantityText,
  type FlowCell, type FlowPayer, type FlowProvider, type LinkBasis, type NetLang,
} from '../../../shared/network.v1.js';
import './FlowMatrix.de.js';
import './FlowMatrix.fr.js';

export interface FlowMatrixProps {
  providers: FlowProvider[];
  payers: FlowPayer[];
  cells: FlowCell[];
  /** The year filter, when one is on; else every pair shows its latest quantified year. */
  year: number | null;
  focus?: number | null;
  onFocus?: (organisationId: number) => void;
}

/** The acronym a body carries in parentheses — "…(CIFÉA)" → "CIFÉA"; else the name itself. */
export function shortName(name: string): string {
  const m = /\(([^()]+)\)\s*$/.exec(name);
  return m ? m[1] : name;
}

/** Which basis a cell shows when a pair carries several: a share first, then heads, places, money. */
const BASIS_ORDER: LinkBasis[] = ['participants_pct', 'revenue_pct', 'participants_count', 'places_count', 'amount_chf'];

/**
 * What one cell shows for a (provider, payer) pair: among the HELD rows (proposals only when
 * nothing is held), the quantified row of the requested year — or, with no year filter, of the
 * pair's latest year — preferring a share to a count to an amount; else the relation row itself.
 * Pure and exported for the test.
 */
export function pickCell(cells: FlowCell[], year: number | null): { cell: FlowCell | null; held: number; proposed: number } {
  const held = cells.filter((c) => !c.proposed);
  const proposed = cells.filter((c) => c.proposed);
  const pool = held.length ? held : proposed;
  const quantified = pool.filter((c) => c.quantity != null && c.basis != null);
  let candidates = quantified;
  if (year != null) {
    candidates = quantified.filter((c) => c.year === year);
  } else {
    const latest = quantified.reduce<number | null>((m, c) => (c.year != null && (m == null || c.year > m) ? c.year : m), null);
    if (latest != null) candidates = quantified.filter((c) => c.year === latest || c.year == null);
  }
  const sorted = [...candidates].sort((a, b) => BASIS_ORDER.indexOf(a.basis as LinkBasis) - BASIS_ORDER.indexOf(b.basis as LinkBasis));
  return { cell: sorted[0] ?? pool[0] ?? null, held: held.length, proposed: proposed.length };
}

/** The ink a share puts on its cell — 0…0.85 so a 100 % cell stays a cell, not a black square. */
function inkOf(cell: FlowCell | null): number {
  if (!cell || cell.proposed || cell.quantity == null) return 0;
  if (cell.basis === 'participants_pct' || cell.basis === 'revenue_pct') return Math.min(1, Math.max(0, cell.quantity / 100)) * 0.85;
  return 0;
}

function fmtNarrow(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '’');
}

export default function FlowMatrix({ providers, payers, cells, year, focus = null, onFocus }: FlowMatrixProps) {
  const t = useT();
  const { lang, fmtNum } = useLocale();
  const nl = lang as NetLang;

  const index = useMemo(() => {
    const m = new Map<string, FlowCell[]>();
    for (const c of cells) {
      const k = `${c.provider}|${c.payer}`;
      (m.get(k) ?? m.set(k, []).get(k)!).push(c);
    }
    return m;
  }, [cells]);

  // Rows and columns both sorted — "there should be logic in the order in which you display
  // information" (Knaflic p. 219): providers by how many payers they name, payers by how many
  // providers name them, so the dense corner is top-left where the eye lands first.
  const rows = useMemo(() => [...providers].sort((a, b) => (b.payers - a.payers) || (b.held - a.held) || a.name.localeCompare(b.name)), [providers]);
  const cols = useMemo(() => [...payers].sort((a, b) => (b.providers - a.providers) || (b.held - a.held) || a.name.localeCompare(b.name)), [payers]);

  if (!rows.length || !cols.length) {
    return <p className="zone-empty">{t('No provider names a payer under these filters, so there is no matrix to draw.')}</p>;
  }

  const cellText = (cell: FlowCell): string => {
    if (cell.quantity == null || !cell.basis) return '';
    switch (cell.basis) {
      case 'participants_pct': return `${cell.quantity} %`;
      case 'revenue_pct': return `${cell.quantity} % ${t('rev.')}`;
      case 'participants_count': return fmtNarrow(cell.quantity);
      case 'places_count': return `${fmtNarrow(cell.quantity)} ${t('pl.')}`;
      case 'amount_chf': return fmtNarrow(cell.quantity);
    }
  };

  return (
    <Sheet clip>
      <div className="mx-wrap">
        <Tbl className="mx">
          <thead>
            <tr>
              <th className="mx-row-head">{t('Provider')}</th>
              {cols.map((p) => (
                <th key={p.key} className="mx-col" title={p.name}>
                  {p.organisation_id != null && onFocus ? (
                    <Lnk onClick={() => onFocus(p.organisation_id!)}>
                      <span className={`mx-col-name${p.roles.includes('payer') ? ' mx-col-name--payer' : ''}`}>{shortName(p.name)}</span>
                    </Lnk>
                  ) : (
                    <span className="mx-col-name">{shortName(p.name)}</span>
                  )}
                </th>
              ))}
              <th className="mx-col" title={t('the Σ of this provider’s mix — computed, carries no mark')}>
                <span className="mx-col-name">{t('attributed')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((prov) => (
              <tr key={prov.key} className={focus === prov.organisation_id ? 'row--focus' : undefined}>
                <td className="mx-row-head">
                  {onFocus
                    ? <Lnk onClick={() => onFocus(prov.organisation_id)}><Ref>{prov.ref}</Ref> {prov.name}</Lnk>
                    : <><Ref>{prov.ref}</Ref> {prov.name}</>}
                </td>
                {cols.map((p) => {
                  const list = index.get(`${prov.key}|${p.key}`);
                  if (!list || !list.length) return <td key={p.key} className="mx-cell" />;
                  const { cell, held, proposed } = pickCell(list, year);
                  if (!cell) return <td key={p.key} className="mx-cell" />;
                  const k = inkOf(cell);
                  const text = cellText(cell);
                  const onlyProposed = held === 0 && proposed > 0;
                  const title = [
                    `${prov.name} · ${linkLabel(nl, 'role', cell.role)}: ${p.name}`,
                    cell.quantity != null && cell.basis ? quantityText(nl, cell.basis, cell.quantity, cell.qualifier) : null,
                    cell.basis ? linkLabel(nl, 'basis', cell.basis) : null,
                    cell.year != null ? String(cell.year) : null,
                    cell.people_computed != null ? `${fmtNum(cell.people_computed)} ${t('people')} (${(cell.people_title ?? '').replace(/^computed: /, '')})` : null,
                    onlyProposed ? t('proposed, not held') : cell.held_by_rule ? t('held by rule') : t('held'),
                    cell.method === 'estimated' ? t('estimated') : null,
                    held + proposed > 1 ? t('{n} rows on this pair', { n: held + proposed }) : null,
                  ].filter(Boolean).join(' · ');
                  const cls = ['mx-cell', k >= 0.45 && 'mx-cell--deep', onlyProposed && 'mx-cell--proposed', k === 0 && text && 'mx-cell--figure']
                    .filter(Boolean).join(' ');
                  return (
                    <td key={p.key} className={cls} style={{ ['--k' as string]: k }} title={title}>
                      {text
                        ? text
                        : onlyProposed ? <Att kind="proposed" title={title} /> : <Att kind="held" title={title} />}
                    </td>
                  );
                })}
                <td className="mx-cell mx-cell--figure" title={prov.attributed_pct != null ? t('Σ of the {basis} mix, {year}', { basis: prov.mix_basis ? linkLabel(nl, 'basis', prov.mix_basis) : '', year: prov.mix_year ?? '' }) : undefined}>
                  {prov.attributed_pct != null ? `${prov.attributed_pct} %` : ''}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="mx-row-head">{t('providers naming it')}</td>
              {cols.map((p) => <td key={p.key}>{p.providers}</td>)}
              <td />
            </tr>
          </tfoot>
        </Tbl>
      </div>
    </Sheet>
  );
}
